import type { EditableRefKind, Meta, RefKind } from '@numistic/contracts'
import { useRefList, useTree } from '@numistic/data'
import { createContext, useContext } from 'react'

const EDITABLE: ReadonlySet<string> = new Set<EditableRefKind>([
  'issuers',
  'periods',
  'currency_units',
  'mints',
  'printers',
  'grading_services',
  'numbering_systems',
])

export interface RefOption {
  id: string
  label: string
  /** Деактивированное значение: не предлагается, но показывается, если уже выбрано. */
  inactive?: boolean
}

/** Варианты для выбора: активные плюс уже выбранные (даже деактивированные). */
export function selectable<T extends { active?: boolean }>(
  options: T[],
  isCurrent: (option: T) => boolean,
): T[] {
  return options.filter((o) => o.active !== false || isCurrent(o))
}

/** Варианты, которые знает только экран (например, разновидности конкретной записи каталога). */
export const ExtraRefOptions = createContext<Partial<Record<RefKind, RefOption[]>>>({})

/** Путь узла в дереве: «Банкноты / По странам / Германия». */
function treeLabels(
  nodes: Array<{ id: string; parentId: string | null; name: string }>,
): RefOption[] {
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const path = (id: string): string => {
    const n = byId.get(id)
    return n ? (n.parentId ? `${path(n.parentId)} / ${n.name}` : n.name) : ''
  }
  return nodes
    .map((n) => ({ id: n.id, label: path(n.id) }))
    .sort((a, b) => a.label.localeCompare(b.label, 'ru'))
}

/** Варианты для поля-ссылки. Коллекции, места хранения и разновидности появятся в этапах 1.4–1.5. */
export function useRefOptions(
  kind: RefKind | null,
  meta: Meta,
): { options: RefOption[]; available: boolean; loading: boolean } {
  const editable = kind !== null && EDITABLE.has(kind)
  // С выключенными: уже выбранное выключенное значение показывается по названию, а не пустым.
  const list = useRefList((editable ? kind : 'issuers') as EditableRefKind, {
    enabled: editable,
    includeInactive: true,
  })
  const extra = useContext(ExtraRefOptions)
  const treeKind = kind === 'collections' ? 'collections' : 'locations'
  const tree = useTree(treeKind, {
    enabled: kind === 'collections' || kind === 'storage_locations',
  })
  if (kind && extra[kind]) return { options: extra[kind]!, available: true, loading: false }
  if (kind === 'collections' || kind === 'storage_locations')
    return { options: treeLabels(tree.data ?? []), available: true, loading: tree.isPending }
  if (kind === 'statuses') {
    return {
      options: meta.statuses.map((s) => ({
        id: s.id,
        label: s.name.ru,
        ...(s.active === false ? { inactive: true } : {}),
      })),
      available: true,
      loading: false,
    }
  }
  if (!editable) return { options: [], available: false, loading: false }
  return {
    options: (list.data ?? []).map((item) => ({
      id: item.id,
      label: 'mark' in item && item.mark ? `${item.name.ru} (${item.mark})` : item.name.ru,
      ...(item.isActive === false ? { inactive: true } : {}),
    })),
    available: true,
    loading: list.isPending,
  }
}
